/** Deep step definitions for tenant-admin page tours. */

function c(targetId, title, body, extra = {}) {
  return {
    targetId,
    title,
    body,
    size: "compact",
    elementType: extra.elementType || "button",
    placement: extra.placement || "bottom",
    action: extra.action,
    ...extra,
  };
}

function d(targetId, title, body, action, outcome, extra = {}) {
  return {
    targetId,
    title,
    body,
    action,
    outcome,
    size: "standard",
    elementType: extra.elementType || "module",
    placement: extra.placement || "bottom",
    tip: extra.tip,
    warning: extra.warning,
    ...extra,
  };
}

function gapPanel(tab, title, body, action, outcome, extra = {}) {
  return d(`tour-gap-admin-panel-${tab}`, title, body, action, outcome, {
    elementType: "panel",
    placement: "top",
    optional: true,
    optionalWaitMs: 500,
    tourEvent: "live-tour:set-tab",
    tourEventDetail: { tab },
    ...extra,
  });
}

function moduleTrackingTour(id, module, title, route, label) {
  const p = `tour-${module}-admin`;
  return {
    id,
    title,
    route,
    description: `${label} cycles, department progress, scores, and reopen actions`,
    steps: [
      d(
        `${p}-header`,
        `${label} tracking header`,
        `Monitor every ${label} assessment in the organisation — cycle name, status, and department progress.`,
        "Confirm you are on the right module before acting.",
        `You always know which ${label} period you are reviewing.`,
        { elementType: "module" },
      ),
      c(
        `${p}-cycle`,
        "Cycle selector",
        "Switch the year/period. Charts and tables below always match this cycle.",
        {
          elementType: "field",
          optional: true,
          optionalWaitMs: 500,
          action: "Pick the current cycle unless you are reviewing history.",
        },
      ),
      c(
        `${p}-new-cycle`,
        "New cycle",
        `Opens a new ${label} assessment window. It becomes the current cycle for new submissions.`,
        {
          elementType: "button",
          placement: "left",
          action: "Create the next period before users need to submit.",
          warning: `Without an open ${label} cycle, users cannot submit.`,
        },
      ),
      d(
        `${p}-empty`,
        "No cycles yet",
        `This empty state appears until you create the first ${label} cycle.`,
        "Click New cycle to start tracking.",
        "Departments can then submit against an open period.",
        { elementType: "card", optional: true, optionalWaitMs: 500 },
      ),
      d(
        `${p}-tracking`,
        "Assessment tracking",
        "Counts of total, draft, submitted, locked, and reopened records, plus completion %.",
        "Chase drafts and reopen only when a department needs to correct answers.",
        "You see the full submission pipeline at a glance.",
        { elementType: "stat", optional: true, optionalWaitMs: 500, placement: "top" },
      ),
      d(
        `${p}-summary`,
        "Department snapshot",
        "Departments in scope, submitted, not started, and average score.",
        "Follow up with departments still on Not started.",
        "Completion and quality are visible together.",
        { elementType: "stat", optional: true, optionalWaitMs: 500 },
      ),
      d(
        `${p}-charts`,
        "Status and score charts",
        "Donut of completion status, overall score gauge, and bar chart of department scores.",
        "Use the bars to spot departments that need attention.",
        "Visual progress without opening each row.",
        { elementType: "card", optional: true, optionalWaitMs: 500, placement: "top" },
      ),
      d(
        `${p}-completion`,
        "Cycle completion bar",
        "Share of departments that have submitted for this cycle, with open/closed status.",
        "Aim for 100% before you close the cycle.",
        "Clear progress toward lock-down.",
        { elementType: "stat", optional: true, optionalWaitMs: 500 },
      ),
      d(
        `${p}-departments`,
        "Department status table",
        "Per-department status, score, submitted date, and Reopen for locked rows.",
        "Use Reopen only when a locked submission must be edited again.",
        "Department-level control of locked work.",
        { elementType: "table", optional: true, optionalWaitMs: 500, placement: "top" },
      ),
      d(
        `${p}-cycles`,
        "Assessment cycles table",
        "All cycles with status, current flag, Set current, Close, and Reopen.",
        "Keep one current open cycle; close when the period ends.",
        "Clean year-on-year programme control.",
        { elementType: "table", optional: true, optionalWaitMs: 500, placement: "top" },
      ),
    ],
  };
}

export const TENANT_PAGE_TOURS_DEEP = [
  {
    id: "tenant-home",
    title: "Tenant Dashboard",
    route: "/tenant",
    description: "Organisation overview, shortcuts, recent users, and license utilisation",
    steps: [
      d(
        "tour-tenant-header",
        "Dashboard header",
        "Your organisation home — tenant admin name and overview context.",
        "Start here after login to scan health before drilling in.",
        "You know whose tenant you are administering.",
        { elementType: "module" },
      ),
      c(
        "tour-tenant-refresh",
        "Refresh",
        "Reloads dashboard stats and license data from the server.",
        {
          elementType: "button",
          placement: "left",
          action: "Click after adding users or changing the license.",
        },
      ),
      c(
        "nav-tenant-admins",
        "Tenant Admins menu",
        "Dropdown for Users, Departments, Gap / PIA / TPRM tracking, User Responses, and Form Questions.",
        {
          elementType: "nav",
          action: "Open this menu to reach every assessment admin screen.",
        },
      ),
      d(
        "tour-tenant-dashboard",
        "Organisation KPIs",
        "Total users, license limit, slots used %, and expiry date.",
        "Check utilisation before adding users.",
        "Avoid surprise license blocks.",
        { elementType: "stat" },
      ),
      d(
        "tour-tenant-quick-actions",
        "Quick action cards",
        "Shortcuts to Users, Email Templates, License, and Settings.",
        "Use these for the most common admin jumps.",
        "Faster tenant administration.",
        { elementType: "card" },
      ),
      c(
        "tour-tenant-qa-users",
        "Add New User",
        "Opens Manage Users so you can create or bulk-upload accounts.",
        { elementType: "card", action: "Go here when onboarding staff." },
      ),
      c(
        "tour-tenant-qa-email",
        "Email Template",
        "Opens branded header/footer settings for outgoing mail.",
        { elementType: "card", action: "Set branding before sending welcomes." },
      ),
      c(
        "tour-tenant-qa-license",
        "License Info",
        "Opens plan, slots, expiry, and enabled modules.",
        { elementType: "card", action: "Check this when you near the user cap." },
      ),
      c(
        "tour-tenant-qa-settings",
        "Settings",
        "Opens profile, SMTP, notifications, and related admin cards.",
        { elementType: "card", action: "Configure mail before inviting users." },
      ),
      d(
        "tour-tenant-recent-users",
        "Recent users",
        "Latest accounts with active/inactive status and a jump to the full list.",
        "Review new accounts; deactivate leavers.",
        "Clean access hygiene.",
        { elementType: "table", placement: "top" },
      ),
      d(
        "tour-tenant-license-panel",
        "License utilisation",
        "Donut of slots used, plan, status, and remaining seats.",
        "Contact platform admin before you hit 100%.",
        "Capacity is visible without leaving the dashboard.",
        { elementType: "card", placement: "top" },
      ),
    ],
  },
  {
    id: "tenant-users",
    title: "Manage Users",
    route: "/tenant/users",
    description: "Create, bulk-upload, search, edit, and deactivate end-user accounts",
    steps: [
      d(
        "tour-users-header",
        "Users page header",
        "Shows slots used vs license limit at the top.",
        "Read the counter before creating accounts.",
        "License-aware user management.",
        { elementType: "module" },
      ),
      c(
        "tour-users-bulk",
        "Bulk Upload",
        "Import many users from CSV instead of adding one by one.",
        {
          elementType: "button",
          placement: "left",
          action: "Use for department-wide onboarding.",
          warning: "Disabled when the license user limit is reached.",
        },
      ),
      c(
        "tour-add-user",
        "Add User",
        "Creates one end-user with email, password, and optional phone.",
        {
          elementType: "button",
          placement: "left",
          action: "Share login credentials securely.",
        },
      ),
      d(
        "tour-users-stats",
        "User statistics",
        "Total, active, inactive, and remaining slots.",
        "Deactivate unused accounts to free slots.",
        "Optimised license usage.",
        { elementType: "stat" },
      ),
      c(
        "tour-users-search",
        "Search and refresh",
        "Filter the list by name or email, then refresh from the server.",
        {
          elementType: "field",
          action: "Search before editing so you open the right row.",
        },
      ),
      d(
        "tour-users-table",
        "Users table",
        "Each row: name, email, status, edit credentials, toggle active, or delete.",
        "Use Edit on a row for updates; deactivate leavers instead of deleting when possible.",
        "Full user lifecycle control.",
        { elementType: "table", placement: "top" },
      ),
    ],
  },
  {
    id: "tenant-departments",
    title: "Departments",
    route: "/tenant/departments",
    description: "Org structure users pick when filling Gap, ROPA, PIA, and TPRM",
    steps: [
      d(
        "tour-dept-header",
        "Departments header",
        "Departments scope every assessment. Users cannot submit until they pick one.",
        "Set these up before users start forms.",
        "Reporting and tracking stay aligned to the org chart.",
        { elementType: "module" },
      ),
      c(
        "tour-dept-bulk",
        "Add Multiple",
        "CSV import for many departments at once.",
        {
          elementType: "button",
          placement: "left",
          action: "Use for first-time org setup.",
        },
      ),
      c(
        "tour-add-department",
        "Add One",
        "Create a single department with ID, name, owner, and email.",
        {
          elementType: "button",
          placement: "left",
          action: "Use for one-off additions.",
        },
      ),
      d(
        "tour-dept-stats",
        "Department statistics",
        "Counts such as total, active, and owners — a snapshot of org coverage.",
        "Keep inactive departments out of live assessments.",
        "You see structure health before opening the table.",
        { elementType: "stat" },
      ),
      c(
        "tour-dept-search",
        "Search and refresh",
        "Filter by name, ID, or owner, then reload the list.",
        {
          elementType: "field",
          action: "Search before edit or deactivate.",
        },
      ),
      d(
        "tour-dept-table",
        "Departments table",
        "ID, name, owner, email, status, Edit, Deactivate / Activate.",
        "Keep names aligned with HR. Deactivate rather than delete if history must remain.",
        "Accurate reporting by department.",
        { elementType: "table", placement: "top" },
      ),
    ],
  },
  {
    id: "tenant-gap",
    title: "Gap Assessment Admin",
    route: "/tenant/gap-assessment",
    description: "Cycles, scores, gaps, evidence, users, and history for Gap Assessment",
    steps: [
      d(
        "tour-gap-admin-header",
        "Gap tracking header",
        "Central dashboard for DPDP gap assessments across all departments.",
        "Read the cycle name in the subtitle before acting.",
        "Data always belongs to one assessment period.",
        { elementType: "module" },
      ),
      c(
        "tour-gap-admin-cycle",
        "Cycle selector",
        "Choose which year/period to view. Every tab below reflects this cycle.",
        {
          elementType: "field",
          action: "Switch here before comparing years.",
        },
      ),
      c(
        "tour-gap-cycle",
        "New Cycle",
        "Opens a new assessment window with year, name, and open/closed status. It becomes current.",
        {
          elementType: "button",
          placement: "left",
          action: "Required before users can submit.",
          warning: "Without an open cycle, Submit is disabled for users.",
        },
      ),
      d(
        "tour-gap-admin-empty",
        "No submissions yet",
        "Shown when the selected cycle has no assessments. Follow the three how-to steps for department leads.",
        "Share the Gap Assessment form with department owners.",
        "You know why the dashboard is empty.",
        { elementType: "card", optional: true, optionalWaitMs: 500, placement: "top" },
      ),
      d(
        "tour-gap-admin-tabs",
        "Admin tabs",
        "Overview, Departments, Users, Compliance, Gaps, Recommendations, Evidence, History, Cycles.",
        "The next steps open each tab so you see the real content.",
        "Full programme visibility without guessing.",
        { elementType: "tab", optional: true, optionalWaitMs: 500 },
      ),
      gapPanel(
        "overview",
        "Overview",
        "Assessment tracking counts plus department/assessment donuts, compliance gauge, and cycle progress.",
        "Start here each cycle to see completion and score at a glance.",
        "You know whether to chase drafts or review gaps next.",
      ),
      d(
        "tour-gap-admin-stats",
        "Tracking stats",
        "Total, draft, submitted, locked, reopened, and completion % for this cycle.",
        "Chase departments below 100% completion.",
        "Pipeline health in one row.",
        { elementType: "stat", optional: true, optionalWaitMs: 500, placement: "top" },
      ),
      gapPanel(
        "departments",
        "Departments tab",
        "Score bars and a table of status, score, gaps, and Reopen per department.",
        "Reopen a locked row only when that department must correct answers.",
        "Department-level ownership of Gap work.",
      ),
      gapPanel(
        "users",
        "Users tab",
        "Who submitted, completion by user, and recent activity (submissions, drafts, reopens).",
        "Use this when you need the person, not just the department.",
        "Accountability for each locked assessment.",
      ),
      gapPanel(
        "compliance",
        "Compliance tab",
        "Overall and department scores, year trends, and sections scoring below target.",
        "Prioritise weak sections before closing the cycle.",
        "Score quality, not just completion.",
      ),
      gapPanel(
        "gaps",
        "Gaps tab",
        "Questions answered No or Partially. Critical gaps are high-severity No answers.",
        "Filter by department and assign owners for the worst items.",
        "A working list of DPDP gaps.",
      ),
      gapPanel(
        "recommendations",
        "Recommendations tab",
        "Comments and notes users added on questions (not auto-generated advice).",
        "Review open comments and mark them addressed as work completes.",
        "Human notes stay attached to the assessment.",
      ),
      gapPanel(
        "evidence",
        "Evidence tab",
        "File attachments uploaded as supporting evidence, plus missing-evidence counts.",
        "Follow up where evidence is missing on locked submissions.",
        "Audit-ready proof beside scores.",
      ),
      gapPanel(
        "history",
        "History tab",
        "Year-over-year scores, gaps, and common comments across past cycles.",
        "Compare this year to last before you close.",
        "Trend, not a single snapshot.",
      ),
      gapPanel(
        "cycles",
        "Cycles tab",
        "Active cycle, selected-cycle completion, and manage open/close/current periods.",
        "Close the period when all departments are locked.",
        "One current cycle at a time.",
      ),
    ],
  },
  moduleTrackingTour(
    "tenant-pia",
    "pia",
    "PIA Tracking",
    "/tenant/pia-tracking",
    "PIA",
  ),
  moduleTrackingTour(
    "tenant-tprm",
    "tprm",
    "TPRM Tracking",
    "/tenant/tprm-tracking",
    "TPRM",
  ),
  {
    id: "tenant-forms",
    title: "Form Questions",
    route: "/tenant/form-questions/gap_assessment",
    description: "Customise Gap, ROPA, PIA, and TPRM questionnaires",
    steps: [
      d(
        "tour-form-header",
        "Form Questions header",
        "One place to manage questions for Gap Assessment, ROPA, PIA, and TPRM.",
        "Pick the form tab before adding or importing questions.",
        "Questionnaires stay consistent across the tenant.",
        { elementType: "module" },
      ),
      c(
        "tour-form-deleted",
        "Deleted Log",
        "Shows questions removed from any form so you can audit what left the library.",
        {
          elementType: "button",
          placement: "left",
          action: "Open after a bulk cleanup to confirm deletions.",
        },
      ),
      c(
        "tour-form-import",
        "Import Defaults",
        "Loads the platform default question set for the selected form. AI evidence may generate per question.",
        {
          elementType: "button",
          placement: "left",
          action: "Use on a new tenant before customising.",
          warning: "Disabled when this form is not in your license.",
        },
      ),
      c(
        "tour-form-bulk",
        "Bulk Upload",
        "CSV import of custom questions for the active form.",
        {
          elementType: "button",
          placement: "left",
          action: "Use when migrating an existing questionnaire.",
        },
      ),
      c(
        "tour-form-add",
        "Add question",
        "Create a single custom question on the active form.",
        {
          elementType: "button",
          placement: "left",
          action: "Add after defaults if you need tenant-specific items.",
        },
      ),
      d(
        "tour-form-questions",
        "Form type tabs",
        "Switch between Gap, ROPA, PIA, and TPRM. Locked tabs mean that module is not in the license.",
        "Click each licensed tab to manage that form.",
        "Tailored questionnaires per module.",
        { elementType: "tab" },
      ),
      d(
        "tour-form-list",
        "Questions list",
        "Edit, reorder, require, or deactivate questions for the selected form.",
        "Review required flags per section before users start.",
        "Maintainable question library.",
        { elementType: "table", placement: "top" },
      ),
    ],
  },
  {
    id: "tenant-responses",
    title: "User Responses",
    route: "/tenant/form-responses",
    description: "Monitor Gap, ROPA, PIA, and TPRM submissions",
    steps: [
      d(
        "tour-responses-header",
        "Responses header",
        "All answers users submitted across assessment types. Click View on a row for full Q&A.",
        "Use this for audit review, not for editing questions.",
        "Central submission monitoring.",
        { elementType: "module" },
      ),
      d(
        "tour-responses-tabs",
        "Form type tabs",
        "Gap Assessment, ROPA, PIA, and TPRM. Locked tabs are outside your license.",
        "The next steps open each licensed tab.",
        "Focused review per module.",
        { elementType: "tab" },
      ),
      c(
        "tour-responses-tab-gap_assessment",
        "Gap Assessment responses",
        "Department Gap answers, scores, comments, and evidence.",
        {
          elementType: "tab",
          optional: true,
          optionalWaitMs: 500,
          requiredModule: "gap",
          tourEvent: "live-tour:set-response-tab",
          tourEventDetail: { tab: "gap_assessment" },
          action: "Open View on a row for the full questionnaire.",
        },
      ),
      c(
        "tour-responses-tab-ropa",
        "ROPA responses",
        "Records of Processing Activities submitted by users.",
        {
          elementType: "tab",
          optional: true,
          optionalWaitMs: 500,
          requiredModule: "ropa",
          tourEvent: "live-tour:set-response-tab",
          tourEventDetail: { tab: "ropa" },
          action: "Switch here to review processing records.",
        },
      ),
      c(
        "tour-responses-tab-pia",
        "PIA responses",
        "Privacy Impact Assessment responses and risk scores.",
        {
          elementType: "tab",
          optional: true,
          optionalWaitMs: 500,
          requiredModule: "dpia",
          tourEvent: "live-tour:set-response-tab",
          tourEventDetail: { tab: "pia" },
          action: "Review PIA risk scores from this tab.",
        },
      ),
      c(
        "tour-responses-tab-tprm",
        "TPRM responses",
        "Third-party risk questionnaire answers and section scores.",
        {
          elementType: "tab",
          optional: true,
          optionalWaitMs: 500,
          requiredModule: "tprm",
          tourEvent: "live-tour:set-response-tab",
          tourEventDetail: { tab: "tprm" },
          action: "Review vendor risk submissions here.",
        },
      ),
      d(
        "tour-responses-table",
        "Submissions table",
        "User, department, status, score, and date. View opens the full question-and-answer detail.",
        "Open View for audit detail on any row.",
        "Audit-ready review of every submission.",
        { elementType: "table", placement: "top" },
      ),
    ],
  },
  {
    id: "tenant-license",
    title: "License & Usage",
    route: "/tenant/license",
    description: "Plan, slots, expiry, and module entitlements",
    steps: [
      d(
        "tour-tenant-license-header",
        "License page header",
        "Subscription details and usage tracking for this organisation.",
        "Open this when you plan a hiring wave or renewal.",
        "License context before the numbers.",
        { elementType: "module" },
      ),
      d(
        "tour-tenant-license-stats",
        "License metrics",
        "Plan, users vs limit, utilisation %, and slots left.",
        "Contact platform admin above 80%.",
        "Proactive license management.",
        { elementType: "stat" },
      ),
      d(
        "tour-tenant-license-detail",
        "License details",
        "Expiry, status, usage bar, and enabled modules (Gap, ROPA, PIA, TPRM, and others).",
        "Verify modules against your contract.",
        "Clear subscription view.",
        { elementType: "card", placement: "top" },
      ),
    ],
  },
  {
    id: "tenant-audit",
    title: "Audit Logs",
    route: "/tenant/audit-logs",
    description: "Organisation-wide trail of tenant admin and user processes",
    steps: [
      d(
        "tour-audit-header",
        "Audit Logs header",
        "Organisation-wide trail of tenant processes — who did what, when, and with which result.",
        "Use this after incidents or for compliance evidence.",
        "You know where the activity history lives.",
        { elementType: "module" },
      ),
      c(
        "tour-audit-refresh",
        "Refresh Logs",
        "Reloads the current page of audit events from the server.",
        {
          elementType: "button",
          placement: "left",
          action: "Refresh after a user reports a failed action.",
        },
      ),
      d(
        "tour-audit-filters",
        "Search and filters",
        "Search actor, action, or module. Filter by HTTP method, role, and success vs error. Export to Excel from the green icon.",
        "Narrow before export so the spreadsheet matches the investigation.",
        "Targeted audit, not a dump of everything.",
        { elementType: "field" },
      ),
      c(
        "tour-audit-export",
        "Export to Excel",
        "Downloads the filtered log set as a CSV Excel can open.",
        {
          elementType: "button",
          placement: "left",
          action: "Export after applying search and filters.",
        },
      ),
      d(
        "tour-audit-table",
        "Audit table",
        "When, actor, role, action, module, method, status, and duration.",
        "Read status codes: 2xx success, 4xx client issues, 5xx server errors.",
        "Forensic trail for tenant processes.",
        { elementType: "table", placement: "top" },
      ),
    ],
  },
  {
    id: "tenant-settings",
    title: "Tenant Settings",
    route: "/tenant/settings",
    description: "Profile, SMTP, email branding, policy prompt, notifications, AI, and breach reminders",
    steps: [
      d(
        "tour-tenant-settings-header",
        "Settings header",
        "Account configuration for this organisation — profile, mail, and module-specific cards below.",
        "Work top to bottom: profile, SMTP, then branding.",
        "One place for tenant configuration.",
        { elementType: "module" },
      ),
      d(
        "tour-tenant-settings-profile",
        "Account profile",
        "Admin name, email, role, and organisation name.",
        "Share the login link and credentials with users.",
        "Users know how to access the app.",
        { elementType: "card" },
      ),
      d(
        "tour-tenant-settings-smtp",
        "Organisation SMTP",
        "Outgoing mail server for welcome letters and notifications. Expand the card to edit host, credentials, and send a test.",
        "Configure host, credentials, then send a test email.",
        "Branded notifications from your domain.",
        { elementType: "panel", placement: "top" },
      ),
      d(
        "tour-tenant-settings-email-branding",
        "Email template branding",
        "Shortcut to customise logo text, colours, copyright, and disclaimer on system emails.",
        "Click Configure to open the full template editor.",
        "Mail matches your organisation identity.",
        { elementType: "card" },
      ),
      d(
        "tour-tenant-settings-email-logs",
        "Email activity logs",
        "Shortcut to the send log for welcome letters, updates, and test alerts.",
        "Click View Logs when a user says they never got mail.",
        "Delivery status without leaving settings.",
        { elementType: "card" },
      ),
      d(
        "tour-tenant-settings-policy-prompt",
        "Policy generation prompt",
        "Customise the system prompt used when this organisation generates AI policies. The same page shows tokens used, tokens left, and estimated cost per policy.",
        "Click Configure to edit the prompt, sentence count, and review token usage.",
        "Policies generated for this organisation follow your wording.",
        { elementType: "card" },
      ),
      d(
        "tour-tenant-settings-ai",
        "AI provider configuration",
        "Connect OpenAI, Anthropic, Groq, Gemini, Azure, or Ollama. Set the token limit and the Maximum Token Usage Percentage for a one-time admin email when usage hits that threshold.",
        "Add a provider, then save the token limit and alert percentage.",
        "AI features run on your chosen vendor.",
        {
          elementType: "panel",
          optional: true,
          optionalWaitMs: 500,
          requiredModule: ["policy_store", "gap"],
          placement: "top",
        },
      ),
      d(
        "tour-tenant-settings-notifications",
        "Notification routing",
        "Which modules email tenant admins vs standard users. Expand to toggle each category.",
        "Turn off noise for modules you do not use.",
        "The right people get the right alerts.",
        { elementType: "panel", placement: "top" },
      ),
      d(
        "tour-tenant-settings-breach",
        "Breach notification settings",
        "Reminder interval for unresolved breach incidents (email alerts to reporters).",
        "Set an interval that matches your incident SLA.",
        "Breach follow-up does not rely on memory.",
        {
          elementType: "panel",
          optional: true,
          optionalWaitMs: 500,
          requiredModule: "breach",
          placement: "top",
        },
      ),
    ],
  },
  {
    id: "tenant-email-templates",
    title: "Email Templates",
    route: "/tenant/email-templates",
    description: "Branded headers, footers, colour presets, test send, and live preview",
    steps: [
      d(
        "tour-email-tpl-header",
        "Email Templates header",
        "Customise headers and footers for organisation emails. Back returns to the previous page.",
        "Edit colours and text on the left; watch the preview on the right.",
        "You know this is branding, not SMTP credentials.",
        { elementType: "module" },
      ),
      d(
        "tour-email-tpl-actions",
        "Template actions",
        "Load Preset restores defaults, Reset falls back to platform branding, Save Template writes your overrides.",
        "Save after you like the preview. Reset only if you want platform defaults.",
        "Safe apply / revert of branding.",
        { elementType: "button", placement: "left" },
      ),
      d(
        "tour-email-tpl-status",
        "Template status",
        "Shows whether custom overrides are active or emails still use platform defaults.",
        "If it says fallback, fill branding and Save.",
        "No surprise unbranded mail.",
        { elementType: "stat" },
      ),
      d(
        "tour-email-tpl-branding",
        "Branding and colours",
        "Colour presets, primary/secondary pickers, company logo text, footer disclaimer, and copyright.",
        "Pick a preset then adjust company name and footer legal text.",
        "Every outgoing system email can match your brand.",
        { elementType: "panel", placement: "top" },
      ),
      d(
        "tour-email-tpl-test",
        "Send test email",
        "Saves changes and sends a real SMTP test to the address you enter.",
        "Send to yourself before inviting users.",
        "You verify rendering and delivery together.",
        { elementType: "field", placement: "top" },
      ),
      d(
        "tour-email-tpl-preview",
        "Live preview",
        "Mock inbox showing To, Subject, and the branded HTML as users will see it.",
        "Watch this while you change colours and footer text.",
        "What you save is what they receive.",
        { elementType: "card", placement: "left" },
      ),
    ],
  },
  {
    id: "tenant-email-logs",
    title: "Email Activity Logs",
    route: "/tenant/email-logs",
    description: "Outgoing mail status, search, and Excel export",
    steps: [
      d(
        "tour-email-logs-header",
        "Email logs header",
        "Organisation outgoing email log — welcome letters, updates, and test alerts.",
        "Open this when someone did not receive mail.",
        "Delivery history in one place.",
        { elementType: "module" },
      ),
      c(
        "tour-email-logs-refresh",
        "Refresh Logs",
        "Reloads the current page of send events.",
        {
          elementType: "button",
          placement: "left",
          action: "Refresh after sending a test or welcome email.",
        },
      ),
      c(
        "tour-email-logs-search",
        "Search logs",
        "Filter by recipient or subject.",
        {
          elementType: "field",
          action: "Search the user's email before concluding mail failed.",
        },
      ),
      c(
        "tour-email-logs-export",
        "Export to Excel",
        "Downloads the filtered send log as CSV.",
        {
          elementType: "button",
          placement: "left",
          action: "Export for an incident or audit pack.",
        },
      ),
      d(
        "tour-email-logs-table",
        "Send log table",
        "Recipient, subject, status, error message, and sent time.",
        "Read Error Message on failed rows to fix SMTP or the address.",
        "Proof of send and failure reasons.",
        { elementType: "table", placement: "top" },
      ),
    ],
  },
];
